import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios, { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, Col, Form, Row } from 'react-bootstrap';
import { Button as ChakraButton } from '@chakra-ui/react';
import { Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { FaFileExcel, FaSearch } from 'react-icons/fa';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

import { ApplicationState } from '../../store';
import { CompanyMaster } from '../../models/CompanyMaster';
import { useAdminContext } from '../../context/AdminProvider';
import * as CompanyMasterStore from '../../store/CompanyMaster';
import InfinitePagination from '../common/InfinitePagination';

type SelectOption = { value: string; label: string };

interface FolderGroupBasic {
    group_id: string;
    group_name: string;
    sid: string;
}

interface FolderGroupListResponse {
    total_count: number;
    result_list: FolderGroupBasic[];
}

interface AdminActionLog {
    id: number;
    log_id: string;
    source_module: string;
    action_type: string;
    target_id?: string;
    performed_by: string;
    sid?: string;
    detail?: string;
    created_on: string;
}

interface LogResponse {
    total_count: number;
    result_list: AdminActionLog[];
}

const ACTION_BADGE_COLOR: Record<string, string> = {
    CREATE: 'primary',
    CREATE_GROUP: 'primary',
    CREATE_YEAR: 'primary',
    UPDATE: 'warning',
    UPDATE_GROUP: 'warning',
    UPDATE_MEMBER: 'warning',
    DELETE: 'danger',
    DELETE_GROUP: 'danger',
    DELETE_MEMBER: 'danger',
    DELETE_USER: 'danger',
    DELETE_YEAR: 'danger',
    ADD_USER: 'info',
    ADD_MEMBER: 'info',
    TOGGLE_ACTIVE: 'secondary',
};

const MODULE_ACTION_OPTIONS: Record<string, SelectOption[]> = {
    'ADM-DMS101': [
        { value: '', label: 'ทั้งหมด' },
        { value: 'CREATE', label: 'CREATE' },
        { value: 'UPDATE', label: 'UPDATE' },
        { value: 'DELETE', label: 'DELETE' },
    ],
    'ADM-DMS102': [
        { value: '', label: 'ทั้งหมด' },
        { value: 'CREATE_GROUP', label: 'CREATE_GROUP' },
        { value: 'UPDATE_GROUP', label: 'UPDATE_GROUP' },
        { value: 'DELETE_GROUP', label: 'DELETE_GROUP' },
        { value: 'ADD_MEMBER', label: 'ADD_MEMBER' },
        { value: 'UPDATE_MEMBER', label: 'UPDATE_MEMBER' },
        { value: 'DELETE_MEMBER', label: 'DELETE_MEMBER' },
    ],
    'ADM-DMS103': [
        { value: '', label: 'ทั้งหมด' },
        { value: 'ADD_USER', label: 'ADD_USER' },
        { value: 'TOGGLE_ACTIVE', label: 'TOGGLE_ACTIVE' },
        { value: 'DELETE_USER', label: 'DELETE_USER' },
    ],
    'ADM-DMS104': [
        { value: '', label: 'ทั้งหมด' },
        { value: 'CREATE', label: 'CREATE' },
        { value: 'UPDATE', label: 'UPDATE' },
        { value: 'DELETE', label: 'DELETE' },
    ],
    'ADM-DMS105': [
        { value: '', label: 'ทั้งหมด' },
        { value: 'CREATE_YEAR', label: 'CREATE_YEAR' },
        { value: 'DELETE_YEAR', label: 'DELETE_YEAR' },
    ],
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    sourceModule: string;
    defaultSid?: string;
};

function AdminActionLogModal({ isOpen, onClose, sourceModule, defaultSid }: Props) {
    const dispatch = useDispatch();
    const companies = useSelector<ApplicationState, CompanyMaster[]>(
        state => state.companyMaster?.responseCompanyMaster?.result_list ?? []
    );
    const { userRole } = useAdminContext();

    useEffect(() => {
        if (isOpen) {
            dispatch(CompanyMasterStore.actionCreators.requestFunctionCompanyMaster(true, 'GET', '', 'active', '') as any);
        } else {
            setHasSearched(false);
        }
    }, [isOpen]);

    const companyOptions: SelectOption[] = useMemo(() => {
        if (companies.length === 0 || userRole.length === 0) return [];
        const hasMasterSid = userRole.some(r => r.sid === '*');
        if (hasMasterSid) return companies.map(c => ({ value: c.sid, label: c.company_name }));
        const allowed = new Set(userRole.map(r => r.sid));
        return companies.filter(c => allowed.has(c.sid)).map(c => ({ value: c.sid, label: c.company_name }));
    }, [companies, userRole]);

    const isCompanyLocked = companyOptions.length === 1;

    const actionTypeOptions: SelectOption[] = MODULE_ACTION_OPTIONS[sourceModule] ?? [{ value: '', label: 'ทั้งหมด' }];

    // Search state
    const [selectedSid, setSelectedSid] = useState('');
    const [sid, setSid] = useState<string | undefined>();
    const [performedBy, setPerformedBy] = useState<string | undefined>();
    const [actionType, setActionType] = useState<string | undefined>();
    const [from, setFrom] = useState<Date | null>(null);
    const [to, setTo] = useState<Date | null>(null);

    // SearchBar local state
    const [sbPerformedBy, setSbPerformedBy] = useState('');
    const [sbActionType, setSbActionType] = useState<SelectOption>(actionTypeOptions[0]);
    const [sbFrom, setSbFrom] = useState<Date | null>(null);
    const [sbTo, setSbTo] = useState<Date | null>(null);

    useEffect(() => {
        if (isOpen && defaultSid) {
            setSelectedSid(defaultSid);
        }
    }, [isOpen, defaultSid]);

    useEffect(() => {
        if (isCompanyLocked && companyOptions.length === 1) {
            setSelectedSid(companyOptions[0].value);
            setSid(companyOptions[0].value);
        }
    }, [isCompanyLocked, companyOptions]);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [hasSearched, setHasSearched] = useState(false);

    const fromISO = useMemo(() => from?.toISOString(), [from]);
    const toISO = useMemo(() => to?.toISOString(), [to]);

    const { data, error, isFetching } = useQuery({
        queryKey: ['admin-action-log', { sourceModule, page, pageSize, sid, performedBy, actionType, fromISO, toISO }],
        queryFn: () =>
            axios.get<LogResponse>('/v1/admin-action-log', {
                headers: { Authorization: `Bearer ${localStorage.getItem('DMS_APPLICATION')}` },
                params: {
                    source_module: sourceModule,
                    page,
                    page_size: pageSize,
                    sid: sid || undefined,
                    performed_by: performedBy || undefined,
                    action_type: actionType || undefined,
                    from_date: fromISO,
                    to_date: toISO,
                },
            }),
        select: r => r.data,
        enabled: isOpen && hasSearched,
        placeholderData: prev => prev,
    });

    useEffect(() => {
        if (!error) return;
        if (error instanceof AxiosError) Swal.fire('Error', error.response?.data?.message || 'ไม่ทราบสาเหตุ', 'error');
        else if (error instanceof Error) Swal.fire('Error', error.message, 'error');
    }, [error]);

    const { data: folderGroups } = useQuery({
        queryKey: ['folder-groups-for-log', selectedSid, sourceModule],
        queryFn: () =>
            axios.get<FolderGroupListResponse>('/v1/folder-group', {
                headers: { Authorization: `Bearer ${localStorage.getItem('DMS_APPLICATION')}` },
                params: selectedSid ? { sid: selectedSid } : {},
            }),
        select: r => r.data.result_list ?? [],
        enabled: isOpen && sourceModule === 'ADM-DMS102',
        staleTime: 5 * 60 * 1000,
    });

    const groupNameMap = useMemo<Record<string, string>>(() => {
        if (!folderGroups) return {};
        return folderGroups.reduce<Record<string, string>>((acc, g) => {
            acc[g.group_id] = g.group_name;
            return acc;
        }, {});
    }, [folderGroups]);

    const handleSearch = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        setSid(selectedSid || undefined);
        setPerformedBy(sbPerformedBy.trim() || undefined);
        setActionType(sbActionType?.value || undefined);
        setFrom(sbFrom);
        setTo(sbTo);
        setHasSearched(true);
    }, [selectedSid, sbPerformedBy, sbActionType, sbFrom, sbTo]);

    const [isExporting, setIsExporting] = useState(false);

    const handleExport = useCallback(async () => {
        setIsExporting(true);
        try {
            const res = await axios.get<LogResponse>('/v1/admin-action-log', {
                headers: { Authorization: `Bearer ${localStorage.getItem('DMS_APPLICATION')}` },
                params: {
                    source_module: sourceModule,
                    page: 1,
                    page_size: 100000,
                    sid: sid || undefined,
                    performed_by: performedBy || undefined,
                    action_type: actionType || undefined,
                    from_date: fromISO,
                    to_date: toISO,
                },
            });

            const rows = res.data.result_list;
            if (!rows || rows.length === 0) {
                Swal.fire('ไม่พบข้อมูล', 'ไม่มีข้อมูลที่จะ Export', 'warning');
                return;
            }

            const sheet = rows.map(log => {
                const row: Record<string, string> = {
                    'Module': log.source_module ?? '',
                    'Action Type': log.action_type ?? '',
                    'Target ID': (() => { if (!log.target_id) return ''; if (log.target_id.startsWith('[')) { try { return JSON.parse(log.target_id).join('\n'); } catch {} } return log.target_id; })(),
                };
                if (isDMS102) row['Group Name'] = groupNameMap[log.target_id ?? ''] ?? '';
                row['Performed By'] = log.performed_by ?? '';
                row['บริษัท'] = exportCompany(log.sid);
                row['Detail'] = (() => { if (!log.detail) return ''; if (log.detail.startsWith('[')) { try { return JSON.parse(log.detail).join('\n'); } catch {} } return log.detail; })();
                row['Timestamp'] = log.created_on ? dayjs(log.created_on).format('DD/MM/YYYY HH:mm:ss') : '';
                return row;
            });

            const ws = XLSX.utils.json_to_sheet(sheet);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'AdminActionLog');
            XLSX.writeFile(wb, `AdminActionLog_${sourceModule}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
        } catch {
            Swal.fire('Error', 'Export ไม่สำเร็จ', 'error');
        } finally {
            setIsExporting(false);
        }
    }, [sourceModule, sid, performedBy, actionType, fromISO, toISO]);

    const selectStyle = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };
    const logs = data?.result_list ?? [];
    const isDMS102 = sourceModule === 'ADM-DMS102';
    const colCount = isDMS102 ? 7 : 6;

    const resolveSids = (rawSid?: string): string[] => {
        if (!rawSid) return [];
        let sids: string[] = [];
        if (rawSid.startsWith('[')) {
            try { sids = JSON.parse(rawSid); } catch { sids = [rawSid]; }
        } else {
            sids = [rawSid];
        }
        return sids.filter(s => companyOptions.some(c => c.value === s));
    };

    const getCompanyLabel = (sidValue: string) =>
        companyOptions.find(c => c.value === sidValue)?.label ?? sidValue;

    const renderCompany = (rawSid?: string) => {
        const sids = resolveSids(rawSid);
        return (
            <>
                {sids.map((s, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <br />}
                        {getCompanyLabel(s)}
                    </React.Fragment>
                ))}
            </>
        );
    };

    const exportCompany = (rawSid?: string) =>
        resolveSids(rawSid).map(getCompanyLabel).join('\n');

    const renderArray = (raw?: string) => {
        if (!raw) return null;
        let items: string[] = [];
        if (raw.startsWith('[')) {
            try { items = JSON.parse(raw); } catch { items = [raw]; }
        } else {
            items = [raw];
        }
        return (
            <>
                {items.map((s, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <br />}
                        {s}
                    </React.Fragment>
                ))}
            </>
        );
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} style={{ maxWidth: '95vw', width: '95vw' }}>
            <ModalHeader toggle={onClose}>{sourceModule} Action Log</ModalHeader>

            <ModalBody>
                {/* Search bar */}
                <form onSubmit={handleSearch}>
                    <Card.Body
                        className="mat-box shadow"
                        style={{ borderColor: '#e5e6e9 #dfe0e4 #d0d1d5', borderStyle: 'solid', borderWidth: '1px' }}
                    >
                        <Row>
                            <Col xs={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>บริษัท :</Form.Label>
                                    <Select
                                        menuPortalTarget={document.body}
                                        styles={selectStyle}
                                        placeholder="เลือก บริษัท"
                                        options={companyOptions}
                                        value={companyOptions.find(c => c.value === selectedSid) ?? null}
                                        onChange={opt => setSelectedSid(opt?.value ?? '')}
                                        isClearable={!isCompanyLocked}
                                        isDisabled={isCompanyLocked}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Performed By :</Form.Label>
                                    <Form.Control
                                        value={sbPerformedBy}
                                        onChange={e => setSbPerformedBy(e.target.value)}
                                        placeholder="ค้นหา User"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={2}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Action Type :</Form.Label>
                                    <Select
                                        menuPortalTarget={document.body}
                                        styles={selectStyle}
                                        options={actionTypeOptions}
                                        value={sbActionType}
                                        onChange={opt => setSbActionType(opt ?? actionTypeOptions[0])}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={2}>
                                <Form.Group className="mb-3">
                                    <Form.Label>From :</Form.Label>
                                    <DatePicker
                                        wrapperClassName="d-block"
                                        selectsStart
                                        selected={sbFrom}
                                        startDate={sbFrom}
                                        endDate={sbTo}
                                        onChange={setSbFrom}
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={5}
                                        timeCaption="time"
                                        dateFormat="dd/MM/yyyy HH:mm"
                                        customInput={<Form.Control />}
                                        isClearable
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={2}>
                                <Form.Group className="mb-3">
                                    <Form.Label>To :</Form.Label>
                                    <DatePicker
                                        wrapperClassName="d-block"
                                        selectsEnd
                                        selected={sbTo}
                                        startDate={sbFrom}
                                        endDate={sbTo}
                                        minDate={sbFrom}
                                        onChange={setSbTo}
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={5}
                                        timeCaption="time"
                                        dateFormat="dd/MM/yyyy HH:mm"
                                        customInput={<Form.Control />}
                                        isClearable
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <ChakraButton
                                    type="submit"
                                    colorScheme="green"
                                    leftIcon={<FaSearch />}
                                    size="sm"
                                    style={{ float: 'right' }}
                                >
                                    ค้นหา
                                </ChakraButton>
                            </Col>
                        </Row>
                    </Card.Body>
                </form>

                <br />

                {/* Page size + Pagination */}
                <Row>
                    <Col xs={6}>
                        <div className="d-flex justify-content-start my-2 my-lg-0">
                            <Form inline>
                                <Label style={{ fontSize: '14px' }}>Show&nbsp;&nbsp;</Label>
                                <Input
                                    type="select"
                                    bsSize="sm"
                                    value={pageSize}
                                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                >
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </Input>
                                <Label style={{ fontSize: '14px' }}>&nbsp;&nbsp;entries</Label>
                            </Form>
                        </div>
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex justify-content-end my-2 my-lg-0">
                            <InfinitePagination
                                disabledNext={logs.length === 0}
                                page={page}
                                style={{ padding: '0px', marginTop: '0px', justifyContent: 'flex-end' }}
                                onChangePage={(_e, p) => setPage(p)}
                            />
                        </div>
                    </Col>
                </Row>

                {/* Table */}
                <Row className="mt-2">
                    <Col>
                        <Table className="table-bordered align-middle employee-font font-size-employee" size="sm">
                            <thead>
                                <tr className="text-center table-active thead-light">
                                    <th>Action Type</th>
                                    <th>Target ID</th>
                                    {isDMS102 && <th>Group Name</th>}
                                    <th>Performed By</th>
                                    <th>บริษัท</th>
                                    <th>Detail</th>
                                    <th>Timestamp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isFetching ? (
                                    <tr>
                                        <td colSpan={colCount} style={{ textAlign: 'center', padding: '20px' }}>
                                            <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" />
                                            {' '}กำลังโหลด...
                                        </td>
                                    </tr>
                                ) : logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={colCount} style={{ background: '#b7b7a4', color: 'black', fontSize: '10pt', textAlign: 'center' }}>
                                            No Data.
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map(log => (
                                        <tr key={log.id}>
                                            <td className="text-center">
                                                <Badge variant={ACTION_BADGE_COLOR[log.action_type] ?? 'secondary'}>
                                                    {log.action_type}
                                                </Badge>
                                            </td>
                                            <td>{renderArray(log.target_id)}</td>
                                            {isDMS102 && (
                                                <td>{groupNameMap[log.target_id ?? ''] ?? '-'}</td>
                                            )}
                                            <td>{log.performed_by}</td>
                                            <td>{renderCompany(log.sid)}</td>
                                            <td>{renderArray(log.detail)}</td>
                                            <td>{log.created_on ? dayjs(log.created_on).format('DD/MM/YYYY HH:mm:ss') : ''}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </ModalBody>

            <ModalFooter>
                <div style={{ flex: 1 }}>
                    <ChakraButton
                        colorScheme="green"
                        size="sm"
                        leftIcon={<FaFileExcel />}
                        isLoading={isExporting}
                        loadingText="Exporting..."
                        onClick={handleExport}
                    >
                        Export Excel
                    </ChakraButton>
                </div>
                <ChakraButton colorScheme="gray" size="sm" onClick={onClose}>
                    Close
                </ChakraButton>
            </ModalFooter>
        </Modal>
    );
}

export default AdminActionLogModal;
